import { type CubeTexture, CubeTextureLoader, type Texture, TextureLoader } from "three";
import { DRACOLoader, type GLTF } from "three/examples/jsm/Addons.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import disposeChild from "@/features/model-renderer/experience/utils/dispose-scene.ts";
import type { Nullable } from "@/types";
import type { Source } from "../types";
import EventEmitter from "./EventEmitter";

type EventTypes = "progress" | "ready";

export type Asset = {
    name: string;
    type: "gltfModel" | "texture" | "cubeTexture";
    path: string | Array<string>;
};

type Items = Record<string, GLTF | Texture | CubeTexture>;

export default class Resources extends EventEmitter<EventTypes> {
    static #instance: Nullable<Resources> = null;

    #sources: ReadonlyArray<Source> = [];
    #items: Items = {} as Items;
    #loaded: number = 0;
    #toLoad: number = Infinity;
    #loaders: Nullable<{
        gltfLoader: GLTFLoader;
        textureLoader: TextureLoader;
        cubeTextureLoader: CubeTextureLoader;
    }>;

    private constructor() {
        super();

        this.#loaders = this.createLoadersFromOrigin(window.location.origin);
    }

    static getInstance(): Resources {
        this.#instance ??= new Resources();
        return this.#instance;
    }

    get items(): Items {
        return this.#items;
    }

    get loaded(): number {
        return this.#loaded;
    }

    get progress(): number {
        return this.#loaded / this.#toLoad;
    }

    get model(): Nullable<GLTF> {
        return (this.items.model as GLTF) ?? null;
    }

    get environmentMap(): Nullable<CubeTexture> {
        return (this.items.environmentMapTexture as CubeTexture) ?? null;
    }

    createLoadersFromOrigin(origin: string) {
        const gltfLoader = new GLTFLoader();
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath(`${origin}/3d-configurator/draco-decoder/`);
        gltfLoader.setDRACOLoader(dracoLoader);

        this.#loaders = {
            gltfLoader: gltfLoader,
            textureLoader: new TextureLoader(),
            cubeTextureLoader: new CubeTextureLoader(),
        };

        return this.#loaders;
    }

    init = (sources: ReadonlyArray<Source>): Resources => {
        this.#loaders ??= this.createLoadersFromOrigin(window.location.origin);

        this.#items = {};
        this.#loaded = 0;
        this.#sources = sources;
        this.#toLoad = sources.length;

        for (const source of this.#sources) {
            const { type } = source;

            switch (source.type) {
                case "gltfModel":
                    this.#loaders.gltfLoader.load(
                        source.path as string,
                        (file) => {
                            this.#items[source.name] = file;
                            this.#onProgress();
                        },
                        undefined,
                        console.error,
                    );
                    break;

                case "texture":
                    this.#loaders.textureLoader.load(source.path as string, (file) => {
                        this.#items[source.name] = file;
                        this.#onProgress();
                    });
                    break;

                case "cubeTexture":
                    this.#loaders.cubeTextureLoader.load(source.path as Array<string>, (file) => {
                        this.#items[source.name] = file;
                        this.#onProgress();
                    });
                    break;

                default:
                    throw new Error(`Unsupported asset type: ${type}`);
            }
        }

        return this;
    };

    destroy() {
        this.model?.scene.traverse(disposeChild);
        this.model?.scene.clear();

        this.#loaders?.gltfLoader?.dracoLoader?.dispose();
        this.#loaders = null;
        this.#items = {};
        this.#loaded = 0;
        this.#toLoad = Infinity;
    }

    #onProgress = (): void => {
        this.#loaded++;
        this.trigger(this.#toLoad === this.#loaded ? "ready" : "progress");
    };
}
